import { clsx } from 'clsx';
import { Search } from 'lucide-react';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: (id: string) => ReactNode;
}

/** Yorliq + xato + izoh. `children` ga `id` beriladi (a11y). */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  return (
    <div className={clsx('kts-field', className)}>
      {label && (
        <label className="kts-field__label" htmlFor={id}>
          {label}
          {required && <span className="req">*</span>}
        </label>
      )}
      {children(id)}
      {error ? <div className="kts-field__error">{error}</div> : hint ? <div className="kts-field__hint">{hint}</div> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...rest }, ref) {
    return <input ref={ref} className={clsx('kts-input', className)} aria-invalid={invalid || undefined} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ className, invalid, ...rest }, ref) {
    return <textarea ref={ref} className={clsx('kts-textarea', className)} aria-invalid={invalid || undefined} {...rest} />;
  },
);

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { options: SelectOption[]; placeholder?: string; invalid?: boolean }
>(function Select({ className, options, placeholder, invalid, ...rest }, ref) {
  return (
    <select ref={ref} className={clsx('kts-select', className)} aria-invalid={invalid || undefined} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
});

export function SearchInput({
  value,
  onChange,
  placeholder = 'Qidirish…',
  className,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className={clsx('kts-input-group', className)}>
      <Search />
      <input
        className="kts-input"
        type="search"
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export const Checkbox = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }>(
  function Checkbox({ label, className, ...rest }, ref) {
    return (
      <label className={clsx('kts-check', className)}>
        <input ref={ref} type="checkbox" {...rest} />
        {label && <span>{label}</span>}
      </label>
    );
  },
);

export const Switch = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }>(
  function Switch({ label, className, ...rest }, ref) {
    return (
      <label className={clsx('kts-switch', className)}>
        <input ref={ref} type="checkbox" role="switch" {...rest} />
        {label && <span>{label}</span>}
      </label>
    );
  },
);

/** Pul kiritish: ko'rinishda guruhlangan (1 250 000), qiymat — son. */
export function MoneyInput({
  value,
  onChange,
  invalid,
  id,
  placeholder = '0',
  disabled,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  invalid?: boolean;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const shown = value === null || value === undefined || Number.isNaN(value) ? '' : Math.round(value).toLocaleString('ru-RU').replace(/,/g, ' ');
  return (
    <div className="kts-input-group">
      <input
        id={id}
        className="kts-input tabular"
        inputMode="numeric"
        value={shown}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '');
          onChange(digits === '' ? null : Number(digits));
        }}
        style={{ paddingRight: 52 }}
      />
      <span className="kts-input-group__suffix">so‘m</span>
    </div>
  );
}
